import { useState, useRef, useEffect, useCallback, Fragment } from 'react'
import { supabase, getSession, SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/supabase'
import { parseMessage } from '../lib/messageFormat'

// =============================================================================
// ACECHAT.JS — Ace AI chat panel (floating assistant)
// =============================================================================
// SECTIONS:
//   [ICONS]       SVG icon components
//   [HELPERS]     MessageText, generateTitle
//   [COMPONENT]   Main AceChat component (state, send, history, render)
// =============================================================================

// Ace sparkle icon
const AceIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" fill="url(#ace-grad)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5"/>
    <defs>
      <linearGradient id="ace-grad" x1="2" y1="2" x2="22" y2="22">
        <stop offset="0%" stopColor="#a78bfa"/>
        <stop offset="50%" stopColor="#818cf8"/>
        <stop offset="100%" stopColor="#6366f1"/>
      </linearGradient>
    </defs>
  </svg>
)

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
)

const HistoryIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
)

// Bold + line breaks, rendered as React elements rather than an HTML string.
// Message content comes from the model and from the user, so neither is trusted
// enough for innerHTML — see src/lib/messageFormat.js.
function MessageText({ text }) {
  return parseMessage(text).map((runs, lineIndex) => (
    <Fragment key={lineIndex}>
      {lineIndex > 0 && <br />}
      {runs.map((run, runIndex) => (
        run.bold
          ? <strong key={runIndex}>{run.text}</strong>
          : <Fragment key={runIndex}>{run.text}</Fragment>
      ))}
    </Fragment>
  ))
}

// Generate a title from the first user message
function generateTitle(messages) {
  const firstUser = messages.find(m => m.role === 'user')
  if (!firstUser) return 'New conversation'
  const text = firstUser.content.trim()
  return text.length > 50 ? text.slice(0, 47) + '...' : text
}

const SUGGESTIONS = [
  'What should I charge for a TikTok deal?',
  'Show me my pipeline health',
  'Which deals need follow-up?',
  'Who are my top-performing agents?',
]

export default function AceChat({ isMobile }) {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [conversationId, setConversationId] = useState(null)
  const [showHistory, setShowHistory] = useState(false)
  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const panelRef = useRef(null)
  const saveTimeoutRef = useRef(null)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => { scrollToBottom() }, [messages, scrollToBottom])
  useEffect(() => { if (open && inputRef.current && !showHistory) inputRef.current.focus() }, [open, showHistory])

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { setShowHistory(false); setOpen(false) } }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // Load most recent conversation when chat opens
  useEffect(() => {
    if (!open) return
    const loadRecent = async () => {
      try {
        const session = await getSession()
        if (!session) return
        const { data } = await supabase
          .from('ace_conversations')
          .select('id, messages, title')
          .eq('user_id', session.user.id)
          .eq('is_archived', false)
          .order('updated_at', { ascending: false })
          .limit(1)
        if (data && data.length > 0 && data[0].messages.length > 0) {
          setConversationId(data[0].id)
          setMessages(data[0].messages)
        }
      } catch (err) {
        console.warn('Could not load conversation:', err)
      }
    }
    if (!conversationId && messages.length === 0) loadRecent()
  }, [open]) // eslint-disable-line

  // Save conversation (debounced)
  const saveConversation = useCallback(async (msgs, convId) => {
    if (!msgs || msgs.length === 0) return convId
    try {
      const session = await getSession()
      if (!session) return convId
      const title = generateTitle(msgs)
      if (convId) {
        // Update existing
        await supabase
          .from('ace_conversations')
          .update({ messages: msgs, title, updated_at: new Date().toISOString() })
          .eq('id', convId)
        return convId
      } else {
        // Create new
        const { data } = await supabase
          .from('ace_conversations')
          .insert({
            user_id: session.user.id,
            org_id: session.user.user_metadata?.org_id || session.user.id,
            messages: msgs,
            title,
          })
          .select('id')
          .single()
        return data?.id || null
      }
    } catch (err) {
      console.warn('Could not save conversation:', err)
      return convId
    }
  }, [])

  // Load conversation history list
  const loadHistory = async () => {
    setHistoryLoading(true)
    try {
      const session = await getSession()
      if (!session) return
      const { data } = await supabase
        .from('ace_conversations')
        .select('id, title, updated_at, messages')
        .eq('user_id', session.user.id)
        .order('updated_at', { ascending: false })
        .limit(30)
      setHistory(data || [])
    } catch (err) {
      console.warn('Could not load history:', err)
    } finally {
      setHistoryLoading(false)
    }
  }

  const openConversation = (conv) => {
    setConversationId(conv.id)
    setMessages(conv.messages || [])
    setShowHistory(false)
    setError(null)
  }

  const startNewChat = async () => {
    // Summarize current conversation into memories before archiving
    if (messages.length >= 2) {
      try {
        const session = await getSession()
        if (session) {
          // Fire and forget — don't block the UI
          fetch(`${SUPABASE_URL}/functions/v1/agency-ai`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              mode: 'summarize',
              messages: messages.map(m => ({ role: m.role, content: m.content })),
              conversation_id: conversationId,
            })
          }).catch(() => {})
        }
      } catch {}
    }
    // Archive current conversation
    if (conversationId && messages.length > 0) {
      try {
        await supabase
          .from('ace_conversations')
          .update({ is_archived: true, updated_at: new Date().toISOString() })
          .eq('id', conversationId)
      } catch {}
    }
    setMessages([])
    setConversationId(null)
    setError(null)
    setShowHistory(false)
  }

  const deleteConversation = async (convId, e) => {
    e.stopPropagation()
    try {
      await supabase.from('ace_conversations').delete().eq('id', convId)
      setHistory(prev => prev.filter(c => c.id !== convId))
      if (convId === conversationId) {
        setMessages([])
        setConversationId(null)
      }
    } catch (err) {
      console.warn('Could not delete:', err)
    }
  }

  const sendMessage = async (text) => {
    if (!text?.trim() || loading) return
    const userMsg = { role: 'user', content: text.trim() }
    const newMessages = [...messages, userMsg]
    setMessages(newMessages)
    setInput('')
    setLoading(true)
    setError(null)

    // Helper: single API attempt with 30s timeout
    const attempt = async () => {
      const session = await getSession()
      if (!session) throw new Error('Not logged in')
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 30000)
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/agency-ai`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            messages: newMessages.map(m => ({ role: m.role, content: m.content }))
          })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Request failed')
        return data
      } finally {
        clearTimeout(timeout)
      }
    }

    try {
      let data
      try {
        data = await attempt()
      } catch (firstErr) {
        // Auto-retry once on network / timeout errors
        if (firstErr.name === 'AbortError' || firstErr.message === 'Failed to fetch') {
          data = await attempt()
        } else {
          throw firstErr
        }
      }

      const fullMessages = [...newMessages, { role: 'assistant', content: data.reply }]
      setMessages(fullMessages)

      // Save to database (debounced)
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = setTimeout(async () => {
        const newId = await saveConversation(fullMessages, conversationId)
        if (newId && !conversationId) setConversationId(newId)
      }, 500)

    } catch (err) {
      const msg = err.name === 'AbortError' ? 'Request timed out — try again'
        : err.message === 'Failed to fetch' ? 'Network error — check your connection and try again'
        : err.message
      setError(msg)
      setMessages(newMessages) // keep user message
      // Still save user message
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = setTimeout(async () => {
        const newId = await saveConversation(newMessages, conversationId)
        if (newId && !conversationId) setConversationId(newId)
      }, 500)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  // Floating button
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Chat with Ace"
        style={{
          position: 'fixed',
          bottom: isMobile ? 16 : 24,
          right: isMobile ? 16 : 24,
          zIndex: 9998,
          width: 56, height: 56,
          borderRadius: '50%',
          border: 'none',
          background: 'linear-gradient(135deg, #7c3aed, #6366f1, #818cf8)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 24px rgba(99,102,241,0.4), 0 0 0 3px rgba(99,102,241,0.15)',
          transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
          animation: 'ace-pulse 3s ease-in-out infinite',
        }}
        onMouseEnter={e => { e.target.style.transform = 'scale(1.1)'; e.target.style.boxShadow = '0 6px 32px rgba(99,102,241,0.5), 0 0 0 4px rgba(99,102,241,0.2)' }}
        onMouseLeave={e => { e.target.style.transform = 'scale(1)'; e.target.style.boxShadow = '0 4px 24px rgba(99,102,241,0.4), 0 0 0 3px rgba(99,102,241,0.15)' }}
      >
        <AceIcon size={28} />
        <style>{`
          @keyframes ace-pulse {
            0%, 100% { box-shadow: 0 4px 24px rgba(99,102,241,0.4), 0 0 0 3px rgba(99,102,241,0.15); }
            50% { box-shadow: 0 4px 24px rgba(99,102,241,0.6), 0 0 0 6px rgba(99,102,241,0.1); }
          }
        `}</style>
      </button>
    )
  }

  // Chat panel
  const panelWidth = isMobile ? '100vw' : 420
  const panelHeight = isMobile ? '100vh' : 600

  return (
    <div ref={panelRef} style={{
      position: 'fixed',
      bottom: isMobile ? 0 : 24,
      right: isMobile ? 0 : 24,
      zIndex: 9998,
      width: panelWidth,
      height: panelHeight,
      borderRadius: isMobile ? 0 : 16,
      overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg)',
      border: isMobile ? 'none' : '1px solid var(--card-border)',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(99,102,241,0.1)',
      animation: 'ace-slide-up 0.3s cubic-bezier(0.4,0,0.2,1)',
    }}>
      <style>{`
        @keyframes ace-slide-up {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .ace-msg-user { background: linear-gradient(135deg, #7c3aed, #6366f1) !important; color: #fff !important; }
        .ace-msg-assistant { background: var(--bg2) !important; color: var(--text) !important; border: 1px solid var(--card-border) !important; }
        .ace-suggestion:hover { background: rgba(99,102,241,0.15) !important; border-color: rgba(99,102,241,0.4) !important; }
        .ace-input:focus { outline: none; box-shadow: 0 0 0 2px rgba(99,102,241,0.3); }
        .ace-history-item:hover { background: rgba(99,102,241,0.08) !important; }
        .ace-history-del:hover { background: rgba(239,68,68,0.15) !important; color: #ef4444 !important; }
      `}</style>

      {/* Header */}
      <div style={{
        padding: '14px 16px',
        background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(8px)',
          }}>
            <AceIcon size={20} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: 'var(--font-display)', letterSpacing: '-0.3px' }}>Ace</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>Agency Command Expert</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory() }} title="Conversation history" style={{
            padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)',
            background: showHistory ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 11,
            cursor: 'pointer', fontWeight: 600, fontFamily: 'var(--font-body)',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <HistoryIcon /> History
          </button>
          <button onClick={startNewChat} title="New chat" style={{
            padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 11,
            cursor: 'pointer', fontWeight: 600, fontFamily: 'var(--font-body)',
          }}>New</button>
          <button onClick={() => { setOpen(false); setShowHistory(false) }} style={{
            padding: '4px 8px', borderRadius: 6, border: 'none',
            background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 16,
            cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center',
          }}>×</button>
        </div>
      </div>

      {/* History panel */}
      {showHistory && (
        <div style={{
          flex: 1, overflowY: 'auto', padding: 12,
          display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 8px', marginBottom: 4 }}>
            Conversations
          </div>
          {historyLoading && (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--text3)', fontSize: 12 }}>Loading...</div>
          )}
          {!historyLoading && history.length === 0 && (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--text3)', fontSize: 12 }}>No conversations yet</div>
          )}
          {history.map(conv => (
            <div
              key={conv.id}
              className="ace-history-item"
              onClick={() => openConversation(conv)}
              style={{
                padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                border: conv.id === conversationId ? '1px solid rgba(99,102,241,0.4)' : '1px solid transparent',
                background: conv.id === conversationId ? 'rgba(99,102,241,0.08)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                transition: 'background 0.15s',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  fontFamily: 'var(--font-body)',
                }}>{conv.title || 'New conversation'}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                  {conv.messages?.length || 0} messages · {new Date(conv.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              </div>
              <button
                className="ace-history-del"
                onClick={(e) => deleteConversation(conv.id, e)}
                title="Delete conversation"
                style={{
                  width: 24, height: 24, borderRadius: 6, border: 'none',
                  background: 'transparent', color: 'var(--text3)',
                  cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'all 0.15s',
                }}
              >×</button>
            </div>
          ))}
        </div>
      )}

      {/* Messages area */}
      {!showHistory && (
        <>
          <div style={{
            flex: 1, overflowY: 'auto', padding: 16,
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {/* Welcome state */}
            {messages.length === 0 && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: 'linear-gradient(135deg, rgba(124,58,237,0.15), rgba(99,102,241,0.15))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <AceIcon size={32} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-display)', marginBottom: 4 }}>Hey, I'm Ace</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5, maxWidth: 280 }}>
                    Your agency's AI partner. I know every deal, every rate, every creator. Ask me anything.
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', maxWidth: 340 }}>
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} className="ace-suggestion" onClick={() => sendMessage(s)} style={{
                      padding: '7px 12px', borderRadius: 20,
                      border: '1px solid var(--card-border)',
                      background: 'var(--bg2)', color: 'var(--text)',
                      fontSize: 11, fontWeight: 500, cursor: 'pointer',
                      fontFamily: 'var(--font-body)', transition: 'all 0.15s',
                    }}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Message bubbles */}
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div className={msg.role === 'user' ? 'ace-msg-user' : 'ace-msg-assistant'} style={{
                  maxWidth: '85%',
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  fontSize: 13, lineHeight: 1.55,
                  fontFamily: 'var(--font-body)',
                  wordBreak: 'break-word',
                }}>
                  <MessageText text={msg.content} />
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  background: 'var(--bg2)', border: '1px solid var(--card-border)',
                  borderRadius: '14px 14px 14px 4px', padding: '12px 18px',
                  display: 'flex', gap: 4, alignItems: 'center',
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#818cf8', animation: 'ace-dot 1.4s ease-in-out infinite' }} />
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#818cf8', animation: 'ace-dot 1.4s ease-in-out 0.2s infinite' }} />
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#818cf8', animation: 'ace-dot 1.4s ease-in-out 0.4s infinite' }} />
                  <style>{`@keyframes ace-dot { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.2); } }`}</style>
                </div>
              </div>
            )}

            {/* Error with retry */}
            {error && (
              <div style={{
                padding: '10px 14px', borderRadius: 8,
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                color: '#ef4444', fontSize: 12, textAlign: 'center',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              }}>
                <span>{error}</span>
                {messages.length > 0 && messages[messages.length - 1].role === 'user' && (
                  <button onClick={() => {
                    const lastUserMsg = messages[messages.length - 1].content
                    setMessages(messages.slice(0, -1))
                    setError(null)
                    setTimeout(() => sendMessage(lastUserMsg), 50)
                  }} style={{
                    padding: '4px 14px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)',
                    background: 'rgba(239,68,68,0.08)', color: '#ef4444', fontSize: 11,
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
                  }}>
                    Retry
                  </button>
                )}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div style={{
            padding: '12px 16px', borderTop: '1px solid var(--card-border)',
            background: 'var(--bg)', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea
                ref={inputRef}
                className="ace-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Ace anything..."
                rows={1}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 12,
                  border: '1px solid var(--card-border)', background: 'var(--bg2)',
                  color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-body)',
                  resize: 'none', lineHeight: 1.4, minHeight: 40, maxHeight: 120,
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading}
                style={{
                  width: 40, height: 40, borderRadius: 12, border: 'none',
                  background: input.trim() && !loading ? 'linear-gradient(135deg, #7c3aed, #6366f1)' : 'var(--bg3)',
                  color: input.trim() && !loading ? '#fff' : 'var(--text3)',
                  cursor: input.trim() && !loading ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'all 0.2s',
                }}
              >
                <SendIcon />
              </button>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', marginTop: 6, opacity: 0.6 }}>
              Ace has real-time access to all agency data
            </div>
          </div>
        </>
      )}
    </div>
  )
}
